import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ScreenSizeDetectorService } from '@serviceplan/ui-kit';
import { ConfigObjectModel, anyAsset } from '@thehubz/client/core';
import { Collection } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { Subscription } from 'rxjs';
import { AssetViewTypeService } from '../../../../services/asset-view-type.service';

@Component({
  selector: 'thehubz-collection-card-view',
  templateUrl: './collection-card-view.component.html',
  styleUrls: ['./collection-card-view.component.scss'],
})
export class CollectionCardViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scroller') scroller!: CdkVirtualScrollViewport;
  @ViewChild('slider') slider!: ElementRef;

  @Input() collectionList!: Array<Collection | Record<string, AnyType>>;
  @Input() collectionSplitList: Array<Collection | Record<string, AnyType>>[] | undefined;
  @Input() gridSettingsKeySuffix = 'CollectionsOverview';
  @Input() configObject!: ConfigObjectModel;
  @Input() assetDetails = '';
  @Input() itemSize = 310;
  AnyAsset = anyAsset;

  @Output() actionButton = new EventEmitter<{ action: string; id: string }>();
  @Output() selectCollection = new EventEmitter<AnyType>();
  @Output() updateCollection = new EventEmitter<AnyType>();

  private resizeObserver?: ResizeObserver;
  private screenSizeSub!: Subscription;

  itemHeight = 0;
  needSliderPagination = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    protected assetViewTypeService: AssetViewTypeService,
    private cdr: ChangeDetectorRef,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {}

  ngAfterViewInit(): void {
    if (this?.scroller?.elementRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollerItemSize();
      });
      this.resizeObserver.observe(this.scroller.elementRef.nativeElement);
    }

    this.screenSizeSub = this.screenSizeDetectorService.screenSize$.subscribe(() => {
      if (this.screenSizeDetectorService.isSmallScreen) {
        // wail until the slider element is present
        setTimeout(() => {
          this.initSlider();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.screenSizeSub.unsubscribe();
  }

  gotoAssetList(collectionId: string | undefined, imageClickFlag?: boolean) {
    if (this.configObject && this.configObject?.callFrom == 'shareCollection') {
      this.activatedRoute.params.subscribe((params) => {
        this.router.navigate(['sharing/collection-asset/' + params['id']]);
      });
    } else {
      if (!imageClickFlag || (imageClickFlag && (!this.configObject || this.configObject.callFrom !== 'dashboard'))) {
        this.router.navigate(['collections/collection-asset-list/' + collectionId]);
      }
    }
  }

  shareCollection(id: string) {
    this.actionButton.emit({ action: 'shareCollection', id });
  }

  deleteCollection(id: string) {
    this.actionButton.emit({ action: 'deleteCollection', id });
  }

  downloadCollection(id: string) {
    this.actionButton.emit({ action: 'downloadCollection', id });
  }

  publishCollection(id: string) {
    this.actionButton.emit({ action: 'publishCollection', id });
  }

  unpublishCollection(id: string) {
    this.actionButton.emit({ action: 'unpublishCollection', id });
  }

  editCollection(id: string) {
    this.actionButton.emit({ action: 'editCollection', id });
  }

  updateScrollerItemSize(): void {
    const item = this.scroller.elementRef.nativeElement.querySelector('.collection-asset-item');

    if (!item) return;

    this.itemHeight = item.clientHeight;
  }

  initSlider(): void {
    if (!this.slider?.nativeElement) return;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const swiperEl = this.slider.nativeElement;
    const paginationEl = swiperEl.parentElement.querySelector('.custom-swiper-pagination');

    const swiperParams = {
      slidesPerView: 1,
      loop: true,
      spaceBetween: 25,
      pagination: {
        el: paginationEl,
        type: 'bullets',
        clickable: true,
      },
      breakpoints: {
        640: {
          slidesPerView: 2,
        },
        1200: {
          slidesPerView: 3,
        },
        1600: {
          slidesPerView: 4,
        },
      },
      on: {
        breakpoint(swiper: AnyType, params: AnyType) {
          that.needSliderPagination = params.slidesPerView < that.collectionList.length;
          that.cdr.detectChanges();
        },
      },
    };

    Object.assign(swiperEl, swiperParams);

    swiperEl.initialize();
  }
}
